/**
 * Carrier metadata for underwriting prompts and the wizard-evals UI.
 *
 * Single source of truth for: display label, single-policy prompt type, and
 * (optional) joint-policy partner prompt type. Adding a new carrier here is
 * sufficient to expose it in the wizard-evals dropdowns and prompt resolution.
 */

import { UNDERWRITING_PROMPT_TYPES } from '../constants';
import type { CarrierName } from './ruleCatalog';

export interface CarrierCatalogEntry {
  name: CarrierName;
  label: string;
  singlePromptType: string;
  partnerPromptType?: string;
}

export const CARRIER_CATALOG: readonly CarrierCatalogEntry[] = [
  {
    name: 'nationwide',
    label: 'Nationwide',
    singlePromptType: UNDERWRITING_PROMPT_TYPES.NATIONWIDE_GUIDANCE,
    partnerPromptType: UNDERWRITING_PROMPT_TYPES.NATIONWIDE_PARTNER_GUIDANCE,
  },
  {
    name: 'oneamerica',
    label: 'OneAmerica',
    singlePromptType: UNDERWRITING_PROMPT_TYPES.ONE_AMERICA_GUIDANCE,
    partnerPromptType: UNDERWRITING_PROMPT_TYPES.ONE_AMERICA_PARTNER_GUIDANCE,
  },
  {
    name: 'lincoln',
    label: 'Lincoln',
    singlePromptType: UNDERWRITING_PROMPT_TYPES.LINCOLN_GUIDANCE,
  },
  {
    name: 'carescout',
    label: 'CareScout',
    singlePromptType: UNDERWRITING_PROMPT_TYPES.CARESCOUT_GUIDANCE,
  },
  {
    name: 'securian',
    label: 'Securian',
    singlePromptType: UNDERWRITING_PROMPT_TYPES.SECURIAN_GUIDANCE,
  },
  {
    name: 'nyl',
    label: 'NYL Asset Flex',
    singlePromptType: UNDERWRITING_PROMPT_TYPES.NYL_ASSET_FLEX_GUIDANCE,
  },
];

export function getCarrierEntry(name: CarrierName): CarrierCatalogEntry {
  const entry = CARRIER_CATALOG.find(c => c.name === name);
  if (!entry) throw new Error(`Unknown carrier: ${name}`);
  return entry;
}

export function carrierSupportsJoint(name: CarrierName): boolean {
  return getCarrierEntry(name).partnerPromptType !== undefined;
}

export function getPromptTypeForCarrier(name: CarrierName, isJoint: boolean): string {
  const entry = getCarrierEntry(name);
  return isJoint && entry.partnerPromptType ? entry.partnerPromptType : entry.singlePromptType;
}
